import { Component, Input, OnChanges } from '@angular/core';

import { LabUploadProblem } from '../../../lab-manager/models/lab-upload-result';

/**
 * The list of things wrong with a submission's results.
 *
 * Shared by the upload dialog and the hand-over check, which show the same list for
 * different reasons. Extracted rather than copied: the two upload subscribe blocks in
 * this module were byte-identical copies that had already drifted apart, and this is
 * the same shape of thing.
 *
 * Presentational only. It takes problems and renders them; what to do about them
 * belongs to whichever dialog is hosting it.
 */
@Component({
  selector: 'app-results-problem-list',
  templateUrl: './results-problem-list.component.html',
  styleUrls: ['./results-problem-list.component.css'],
  standalone: false
})
export class ResultsProblemListComponent implements OnChanges {

  /**
   * How many are listed before the rest become a count.
   *
   * A file read against the wrong layout produces a problem per cell. Nobody reads
   * the eight hundredth, and rendering them all builds that many DOM nodes inside a
   * modal.
   */
  static readonly MAX_SHOWN = 50;

  @Input() problems: LabUploadProblem[] = [];

  /** Problems the API found but did not send, because it caps its list too. */
  @Input() problemsOmitted = 0;

  shown: LabUploadProblem[] = [];
  hiddenCount = 0;

  ngOnChanges(): void {
    const problems = this.problems ?? [];
    this.shown = problems.slice(0, ResultsProblemListComponent.MAX_SHOWN);

    // Two caps compound: the API sends at most a couple of hundred, and this shows at
    // most fifty of those. Both are counted or the line under-reports.
    this.hiddenCount = Math.max(0, problems.length - this.shown.length)
      + (this.problemsOmitted ?? 0);
  }

  isBlockingProblem(problem: LabUploadProblem): boolean {
    return problem?.severity === 'BLOCKING';
  }

  /** "Row 4, sample 100-1-A1", leaving out whichever part is not known. */
  locationOf(problem: LabUploadProblem): string {
    const parts: string[] = [];
    if (problem?.rowNumber != null) {
      parts.push(`Row ${problem.rowNumber}`);
    }
    if (problem?.sampleNumber) {
      parts.push(`sample ${problem.sampleNumber}`);
    }
    return parts.join(', ');
  }
}
